/**
 * The Results tab's Picks view (ISSUE-1317): every member's locked picks in
 * one round, the web twin of the legacy Pick History tab.
 *
 * A round menu (from `POST /round/list`), a player menu and a result filter
 * sit above one row per pick: who, which team, against whom, and the result.
 * The rows come from `POST /results/get-by-round`, which only ever returns
 * picks that have locked, so a pick nobody should see yet never reaches this
 * screen. The read never caches, because grading is what changes it and no
 * mutation in this app can invalidate it. Once the round locks, the same read
 * names who made no pick, shown as "Missed" or "Skipped" rows (ISSUE-1317).
 *
 * A pool is a group of friends, so one round's picks are a short list. The
 * endpoint answers a whole round in one pass and this view renders it all;
 * the player menu is how a fan narrows a big pool down.
 */
import {
  useResultsGetByRound,
  useRoundList,
} from "@sportsmet/contest-sdk/private";
import { Button, cn, InlineBanner, Select, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { extractErrorCode } from "../../errors/apiErrorBus";
import { useTeamNames } from "../pick/useTeamNames";
import {
  buildMemberOptions,
  buildRoundPickRows,
  filterRoundPickRows,
  opponentLine,
  resolveRoundId,
  ROUND_PICKS_CORRECTED_LINE,
  ROUND_PICKS_EVERYONE_VALUE,
  ROUND_PICKS_FAILED_MESSAGE,
  ROUND_PICKS_FILTERED_EMPTY_LINE,
  ROUND_PICKS_LOADING_LABEL,
  ROUND_PICKS_MEMBER_LABEL,
  ROUND_PICKS_NO_ROUNDS_LINE,
  ROUND_PICKS_NONE_LOCKED_LINE,
  ROUND_PICKS_OUTCOME_FILTERS,
  ROUND_PICKS_OUTCOME_GROUP_LABEL,
  ROUND_PICKS_RETRY_LABEL,
  ROUND_PICKS_ROUND_LABEL,
  ROUND_PICKS_YOU_TAG,
  type typeRoundNonPicker,
  type typeRoundPickRow,
  type typeRoundPicksOutcomeFilter,
  type typeRoundPicksWireGame,
} from "./roundPicksModel";

export const RoundPicksList = ({
  poolId,
  contestId,
  onOpenMember,
}: {
  poolId: string;
  contestId: string;
  /** Opens that pick's member, the way a legacy pick history row does. */
  onOpenMember?: ((entryId: string) => void) | undefined;
}): ReactNode => {
  const [requestedRoundId, setRequestedRoundId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<typeRoundPicksOutcomeFilter>("all");
  const [memberEntryId, setMemberEntryId] = useState<string>(
    ROUND_PICKS_EVERYONE_VALUE,
  );

  const roundList = useRoundList(
    { poolId, contestId, limit: 500 },
    { enabled: contestId !== "" },
  );
  const rounds = roundList.data?.data?.items ?? [];
  const roundId = resolveRoundId(rounds, requestedRoundId);

  const picks = useResultsGetByRound(
    { poolId, contestId, roundId: roundId ?? "" },
    {
      enabled: contestId !== "" && roundId !== null,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  const roundsFailed =
    roundList.isError || extractErrorCode(roundList.data) !== null;
  const picksFailed = picks.isError || extractErrorCode(picks.data) !== null;

  if (roundsFailed || picksFailed) {
    return (
      <div data-testid="round-picks-failed">
        <InlineBanner
          variant="warning"
          message={ROUND_PICKS_FAILED_MESSAGE}
          action={{
            label: ROUND_PICKS_RETRY_LABEL,
            onAction: () => {
              void roundList.refetch();
              if (roundId !== null) void picks.refetch();
            },
          }}
        />
      </div>
    );
  }

  if (roundList.data === undefined) {
    return (
      <div data-testid="round-picks-loading">
        <Skeleton shape="card" label={ROUND_PICKS_LOADING_LABEL} />
      </div>
    );
  }

  if (rounds.length === 0 || roundId === null) {
    return (
      <p
        className="text-body text-ink-secondary"
        data-testid="round-picks-no-rounds"
      >
        {ROUND_PICKS_NO_ROUNDS_LINE}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="round-picks">
      <div className="grid gap-3 md:grid-cols-2">
        <Select
          label={ROUND_PICKS_ROUND_LABEL}
          items={rounds.map((round) => ({
            value: round.roundId,
            label: round.label,
          }))}
          value={roundId}
          onValueChange={(next) => {
            setRequestedRoundId(next);
            setMemberEntryId(ROUND_PICKS_EVERYONE_VALUE);
          }}
          name="round-picks-round"
        />
        {picks.data?.data === undefined || picks.data.data === null ? null : (
          <MemberSelect
            games={picks.data.data.games}
            nonPickers={picks.data.data.nonPickers}
            value={memberEntryId}
            onValueChange={setMemberEntryId}
          />
        )}
      </div>
      <div
        role="group"
        aria-label={ROUND_PICKS_OUTCOME_GROUP_LABEL}
        className="flex flex-wrap gap-2"
        data-testid="round-picks-outcome-filter"
      >
        {ROUND_PICKS_OUTCOME_FILTERS.map((filter) => {
          const isActive = filter.value === outcome;
          return (
            <Button
              key={filter.value}
              type="button"
              size="sm"
              className="rounded-full"
              variant={isActive ? "primary" : "secondary"}
              aria-pressed={isActive}
              data-testid={`round-picks-outcome-${filter.value}`}
              onClick={() => {
                setOutcome(filter.value);
              }}
            >
              {filter.label}
            </Button>
          );
        })}
      </div>
      {picks.data?.data === undefined || picks.data.data === null ? (
        <div data-testid="round-picks-loading">
          <Skeleton shape="card" label={ROUND_PICKS_LOADING_LABEL} />
        </div>
      ) : (
        <RoundPickRows
          poolId={poolId}
          games={picks.data.data.games}
          nonPickers={picks.data.data.nonPickers}
          isCorrected={
            picks.data.data.correctedAt !== undefined &&
            picks.data.data.correctedAt !== null
          }
          outcome={outcome}
          memberEntryId={memberEntryId}
          onOpenMember={onOpenMember}
        />
      )}
    </div>
  );
};

/**
 * The player menu is built from the round's own picks, so it only ever offers
 * people a fan can actually filter to. Team names are not needed for it, so
 * it does not wait on them.
 */
function MemberSelect(props: {
  games: readonly typeRoundPicksWireGame[];
  nonPickers: readonly typeRoundNonPicker[];
  value: string;
  onValueChange: (next: string) => void;
}): ReactNode {
  const options = buildMemberOptions(
    buildRoundPickRows(props.games, new Map(), props.nonPickers),
  );
  const value = options.some((option) => option.value === props.value)
    ? props.value
    : ROUND_PICKS_EVERYONE_VALUE;
  return (
    <Select
      label={ROUND_PICKS_MEMBER_LABEL}
      items={options}
      value={value}
      onValueChange={props.onValueChange}
      name="round-picks-member"
    />
  );
}

/**
 * Mounts only once the picks have arrived, because `useTeamNames` has no
 * `enabled` switch. While the names load the skeleton stays, so a fan never
 * sees a team id turn into a name.
 */
function RoundPickRows(props: {
  poolId: string;
  games: readonly typeRoundPicksWireGame[];
  nonPickers: readonly typeRoundNonPicker[];
  isCorrected: boolean;
  outcome: typeRoundPicksOutcomeFilter;
  memberEntryId: string;
  onOpenMember?: ((entryId: string) => void) | undefined;
}): ReactNode {
  const teamNames = useTeamNames({ poolId: props.poolId });
  if (teamNames.isLoading) {
    return (
      <div data-testid="round-picks-loading">
        <Skeleton shape="card" label={ROUND_PICKS_LOADING_LABEL} />
      </div>
    );
  }
  const allRows = buildRoundPickRows(
    props.games,
    teamNames.teamsById,
    props.nonPickers,
  );
  const rows = filterRoundPickRows(allRows, {
    outcome: props.outcome,
    entryId: props.memberEntryId,
  });

  return (
    <div className="flex flex-col gap-2">
      {props.isCorrected ? (
        <p
          className="text-caption text-ink-secondary"
          data-testid="round-picks-corrected"
        >
          {ROUND_PICKS_CORRECTED_LINE}
        </p>
      ) : null}
      {allRows.length === 0 ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="round-picks-none-locked"
        >
          {ROUND_PICKS_NONE_LOCKED_LINE}
        </p>
      ) : rows.length === 0 ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="round-picks-filtered-empty"
        >
          {ROUND_PICKS_FILTERED_EMPTY_LINE}
        </p>
      ) : (
        <ul data-testid="round-picks-rows">
          {rows.map((row) => (
            <RoundPickRow
              key={row.key}
              row={row}
              logoUrl={
                row.participation === "picked"
                  ? (teamNames.teamsById.get(row.teamId)?.logoUrl ?? null)
                  : null
              }
              onOpenMember={props.onOpenMember}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function RoundPickRow(props: {
  row: typeRoundPickRow;
  logoUrl: string | null;
  onOpenMember?: ((entryId: string) => void) | undefined;
}): ReactNode {
  const { row } = props;
  const body = (
    <>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="break-words text-body text-ink">
          {row.displayName}
          {row.isViewer ? (
            <span className="text-caption text-ink-secondary">
              {" "}
              {ROUND_PICKS_YOU_TAG}
            </span>
          ) : null}
        </span>
        {row.participation === "picked" ? (
          <>
            <span className="flex items-center gap-2 text-caption text-ink">
              {props.logoUrl === null ? null : (
                <img
                  src={props.logoUrl}
                  alt=""
                  className="size-4 shrink-0 object-contain"
                />
              )}
              <span className="min-w-0 break-words">{row.teamName}</span>
            </span>
            <span className="text-caption text-ink-tertiary">
              {opponentLine(row.opponentName)}
            </span>
          </>
        ) : (
          <>
            <span
              className={cn(
                "text-caption",
                row.participation === "missed"
                  ? "text-error"
                  : "text-ink-secondary",
              )}
              data-testid={`round-pick-no-pick-${row.entryId}`}
            >
              {row.label}
            </span>
            <span className="text-caption text-ink-tertiary">{row.detail}</span>
          </>
        )}
      </span>
      {row.participation === "picked" ? (
        <span
          className={cn(
            "shrink-0 rounded-full border border-hairline px-2 py-1 text-caption",
            row.outcome === "win" && "text-success",
            row.outcome === "loss" && "text-error",
            (row.outcome === "pending" || row.outcome === "push") &&
              "text-ink-secondary",
          )}
          data-testid={`round-pick-outcome-${row.key}`}
        >
          {row.outcomeLabel}
        </span>
      ) : null}
    </>
  );
  const rowClass = "flex w-full items-center gap-3 px-4 py-2 text-left";
  return (
    <li
      className="border-t border-hairline"
      data-testid={`round-pick-${row.key}`}
    >
      {props.onOpenMember === undefined ? (
        <div className={rowClass}>{body}</div>
      ) : (
        <button
          type="button"
          aria-haspopup="dialog"
          className={cn(
            rowClass,
            "min-h-touch-target transition-colors duration-200 ease-standard hover:bg-surface-sunken motion-reduce:transition-none",
            "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring",
          )}
          onClick={() => {
            props.onOpenMember?.(row.entryId);
          }}
        >
          {body}
        </button>
      )}
    </li>
  );
}
